import { Box, Group, Stack, TextInput } from '@mantine/core';
import type { Team } from '../../../api/projects-client/projects-client.type';
import type { Outcome } from '../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { baseWhite, inkLight } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { StrategyEntityTable } from '../components/strategy-entity-table';
import { MainSectionExportActions } from '../initiative-list/main-section/main-section-export-actions';

type Props = {
  filteredOutcomes: Outcome[];
  teams: Team[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedOutcomeId?: string;
  onOutcomeClick?: (outcome: Outcome) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  tableSortBy: string;
  tableSortDirection: 'asc' | 'desc';
  onSortChange: (sortBy: string, direction: 'asc' | 'desc') => void;
  onExportCSV: () => void;
  onCreatePresentation: () => void;
  canExportPresentations: boolean;
};

export function OutcomesMainSectionView({
  filteredOutcomes,
  teams,
  searchTerm,
  onSearchChange,
  selectedOutcomeId,
  onOutcomeClick,
  currentPage,
  onPageChange,
  tableSortBy,
  tableSortDirection,
  onSortChange,
  onExportCSV,
  onCreatePresentation,
  canExportPresentations,
}: Props) {
  const i18n = useTranslation();
  return (
    <Stack h="100%" style={{ overflow: 'hidden' }} gap="xs">
      <Group justify="space-between" miw={760}>
        <Group gap={16}>
          <Button
            variant="secondary"
            radius="xl"
            size="sm"
            leftSection={<Icon name="format_list_bulleted" color={baseWhite} size={16} style={{ cursor: 'pointer' }} />}
          >
            Table View
          </Button>
        </Group>
        <Group>
          <TextInput
            placeholder={`Search by ${i18n.t('common.outcome').toLowerCase()} name or ID`}
            radius="xl"
            size="sm"
            value={searchTerm}
            leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
            style={{ width: 300 }}
            onChange={(event) => onSearchChange(event.currentTarget.value)}
          />
          <MainSectionExportActions
            disabled={filteredOutcomes.length === 0}
            onExportCSV={onExportCSV}
            onCreatePresentation={onCreatePresentation}
            canExportPresentations={canExportPresentations}
          />
        </Group>
      </Group>

      <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <StrategyEntityTable<Outcome>
          entities={filteredOutcomes}
          entityType="outcome"
          onEntityClick={onOutcomeClick || (() => {})}
          selectedEntityId={selectedOutcomeId}
          page={currentPage}
          onPageChange={onPageChange}
          tableSortBy={tableSortBy}
          tableSortDirection={tableSortDirection}
          onSortChange={onSortChange}
          showTeamsColumn={true}
          teams={teams}
          filtersApplied={true}
        />
      </Box>
    </Stack>
  );
}
